// Report — the document generated in the field. Acta, informe, punch list,
// bitácora. Belongs to an Asset (and through it to an Organization).
//
// `parties`, `signatures` and `adminData` are JSONB columns. parties and
// signatures are validated through schemas in parties.ts and signatures.ts;
// adminData is intentionally schemaless (meters, keys, conditions, etc. that
// shift between recipes) — recipes can wrap their own validation around it.
//
// `address` and `unit` are denormalized from Asset at creation time. v2
// proved this matters: the asset address can change (renumbering, fix to
// owner's records) but a signed report must always render the exact value
// that was on it when signed.
import { isoDateTimeSchema, slugSchema } from "@bitacorapro/shared";
import { z } from "zod";
import { reportStatusSchema } from "../enums.js";
import { assetIdSchema, checklistTemplateIdSchema, organizationIdSchema, reportIdSchema, userIdSchema, } from "../ids.js";
import { partiesSchema } from "../parties.js";
import { constanciaMotivoSchema, signaturesSchema } from "../signatures.js";
export const reportAdminDataSchema = z.record(z.string(), z.unknown());
// Capture-time context metadata (#0033). Used by facility verticals to
// confirm an inspector was on-site when generating an acta — geo + a
// device snapshot. All fields are optional ("capture-with-flag"
// approach): if the user denies the location prompt or GPS is
// unreliable, the report is still created, just without the data.
// Admins can later see the flag and decide whether to question the
// acta. La Fete may eventually ask for hard-block-on-no-permission;
// captured-with-flag is the demo-day default.
// Geo evidence shape — reused by contextMetadata.geo (capture time, when
// the inspector taps "Nueva acta") and by reportSignBody.signedGeo /
// reports.signed_geo (sign time, #38). Shared so the two capture points
// cannot drift. `capturedAt` reads correctly for both phases ("when this
// reading was taken").
export const reportGeoSchema = z
    .object({
    // Why an enum: each value is a different signal to the admin
    // reviewing the acta. denied = active refusal; prompt-skipped =
    // the SPA didn't ask (e.g. permission API call errored);
    // unsupported = browser lacks geolocation entirely; timeout =
    // permission granted but the GPS fix didn't arrive in time
    // (cold GPS on a new phone — code 3); position-unavailable =
    // the OS couldn't determine location at all (code 2); error =
    // any other / non-spec GeolocationPositionError code.
    // Keep in sync with CapturedGeo.permission in
    // apps/web-svelte/src/lib/util/captureGeo.ts.
    permission: z.enum([
        "granted",
        "denied",
        "prompt-skipped",
        "unsupported",
        "timeout",
        "position-unavailable",
        "error",
    ]),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    // Browser-reported accuracy circle in meters. ±50m on a phone
    // outdoors, ±500-2000m indoors / on cell-tower fallback.
    accuracyMeters: z.number().nonnegative().optional(),
    capturedAt: isoDateTimeSchema.optional(),
    // Reverse-geocoded human address for this fix (ADR-0039). Resolved
    // client-side at write time (Google Geocoding), bundled into this same
    // blob in the one-shot transaction that writes the coordinates — so it
    // is never written separately and the ADR-0034 JSONB-clobber hazard
    // does not apply. Read paths render this string instead of re-querying
    // Google. Distinct from reportSchema.address (the typed property
    // address): this is what the GPS fix resolved to. Optional — absent on
    // denial / timeout / no-key / no-Geocoding-API (capture-with-flag).
    resolvedAddress: z.string().trim().max(500).optional(),
})
    .strict();
export const reportContextMetadataSchema = z
    .object({
    geo: reportGeoSchema.optional(),
    device: z
        .object({
        userAgent: z.string().max(500).optional(),
        screenWidth: z.number().int().nonnegative().optional(),
        screenHeight: z.number().int().nonnegative().optional(),
        timezone: z.string().max(80).optional(),
        language: z.string().max(20).optional(),
    })
        .strict()
        .optional(),
})
    .strict();
export const reportSchema = z.object({
    id: reportIdSchema,
    organizationId: organizationIdSchema,
    assetId: assetIdSchema,
    authorUserId: userIdSchema,
    // Recipe-driven (e.g. `check_in`, `check_out`, `visita_preventiva`,
    // `punch_list`). Validated against recipe.reportTypes by invariants.
    reportType: slugSchema,
    // Snapshot from asset at creation time. See above.
    address: z.string().trim().min(1).max(500),
    unit: z.string().trim().max(50).optional(),
    // Linked report — a check-out is linked to its corresponding check-in.
    // Optional for verticals where reports stand alone.
    relatedReportId: reportIdSchema.optional(),
    status: reportStatusSchema,
    parties: partiesSchema,
    signatures: signaturesSchema,
    adminData: reportAdminDataSchema,
    contextMetadata: reportContextMetadataSchema.default({}),
    summary: z.string().max(20_000).optional(),
    pdfUrl: z.string().url().optional(),
    // Per-acta numeric score 1.00-5.00 (ADR-0026). Null until signed (or
    // for verticals with no checklist model). Computed by computeReportScore
    // and written co-transactionally with signedAt — never recomputed
    // client-side (that drift is what produced the badge/score bug this
    // field fixes).
    score: z.number().min(1).max(5).nullable().optional(),
    signedAt: isoDateTimeSchema.optional(),
    // ADR-0098 Decision 3 — the universal terminal timestamp, set by both
    // closure mechanisms (signature via /sign, attestation via the future
    // POST /reports/:id/close, #0509) at the same instant as signedAt when
    // signed, or alone when attestation-closed (signedAt stays null in that
    // case). isReportTerminal() below is the single predicate reading it.
    closedAt: isoDateTimeSchema.optional(),
    // Sign-time geolocation (#38). Distinct from contextMetadata.geo
    // (capture time) — this is where the signing happened. Written
    // co-transactionally with signedAt; absent on older signed rows and
    // on clients that can't supply location (capture-with-flag).
    signedGeo: reportGeoSchema.optional(),
    // Constancia "no firmó" (ADR-0028, H-03). All three fields are
    // either all undefined (acta firmada por ambas partes) or all set
    // (acta firmada con constancia). The free text accompanying
    // motivo='otro' lives on the signature itself (signature.declineNote)
    // — not as a fourth column here — so report-level rendering picks
    // it up from `signatures[].declineNote` for the declined role.
    constanciaNoFirmaMotivo: constanciaMotivoSchema.optional(),
    constanciaNoFirmaRole: slugSchema.optional(),
    constanciaNoFirmaAt: isoDateTimeSchema.optional(),
    // Public verification identifier (ADR-0062). Printed on the signed PDF
    // and resolved by GET /public/verify/:folio. Null until signedAt is
    // set; written co-transactionally with signedAt (see buildReportFolio
    // below) and never recomputed afterward — same immutability posture as
    // findingsSnapshot/signedGeo. Distinct from findings.folio (ADR-0059,
    // per-observation, broker_postventa-only) — different namespace, same
    // report row is never involved in that allocator.
    folio: z.string().optional(),
    // Which checklist_templates row this acta ran (ADR-0068, #0390). Null
    // for verticals with no checklist model (residential_rental,
    // broker_postventa) and for historical reports created before this
    // column existed. Set once at creation via POST /reports and never
    // changed afterwards — "one acta = one template for its entire
    // lifecycle" is a load-bearing invariant (see GET /reports/:id/blocks,
    // #0378). Nullable rather than just-optional because the DB column is
    // permanently nullable, not merely absent-until-backfilled.
    checklistTemplateId: checklistTemplateIdSchema.nullable().optional(),
    createdAt: isoDateTimeSchema,
});
// ADR-0062 — the report-level public-verification folio. Pure function of
// two immutable inputs (report id, signedAt), so unlike ADR-0059's
// counter-derived finding folio it cannot drift on re-render — recompute
// and stored-value always agree. Ported from the previous client-side-only
// apps/web-svelte/src/lib/pdf/generator.ts `buildFolio`; this is now the
// single source of truth core-api stamps into reports.folio and the PDF
// generator reads back rather than recomputing.
export function buildReportFolio(reportId, signedAt) {
    const year = new Date(signedAt ?? Date.now()).getFullYear();
    const digest = reportId.replace(/-/g, "").slice(0, 8).toUpperCase();
    return `ACT-${year}-${digest}`;
}
// ADR-0098 Decision 3-4 — the shared, service-agnostic terminal predicate.
// Structural parameter type (not `typeof reports.$inferSelect`, which is a
// core-api-only Drizzle type) so both core-api's and ai-svc's independently
// declared `reports` mirror tables satisfy it with no import between the
// two services — core-engine stays framework-free (no drizzle-orm
// dependency) and neither service's schema becomes a compile-time
// dependency of the other's. The `status === "signed"` arm is a
// backfill-independent fallback: closedAt is backfilled onto every
// historical signed row by `backfill:report-closed-at`, but that backfill
// has not been verified to have run against production (#0587), so a
// bare `closedAt !== null` check would be fail-open for any un-backfilled
// signed row. Drop the status arm once #0587 confirms the backfill is
// complete. The Drizzle SQL-expression form of this same predicate lives
// separately in each service (core-api: lib/report-terminal-sql.ts) —
// never shared, since that would require a shared Drizzle table object.
// `closedAt` is declared optional (`?:`), not required-with-undefined-in-
// the-union — `reportSchema`'s `.optional()` field infers as an optional
// TS property, and a required-form signature here rejects any wire-typed
// caller (e.g. AssetReportHistoryItem) even though the value is read
// identically either way.
export function isReportTerminal(report) {
    return report.closedAt != null || report.status === "signed";
}
// The instant a report became terminal, for value comparisons (e.g. an
// evidence-visibility cutoff: "hide anything created after this instant").
// closedAt is the primary source; the signedAt fallback exists for the
// same backfill-incompleteness reason isReportTerminal() above keeps its
// status arm — a signed report whose closedAt backfill never ran still
// needs a correct (non-null) cutoff rather than falling through to no
// cutoff at all. Returns null only when the report is genuinely not
// terminal. Delete the signedAt fallback arm once #0587 confirms the
// closedAt backfill is complete in production.
// Generic over the timestamp representation (string on the wire, Date in a
// Drizzle row) so a caller with string-typed fields gets `string | null`
// back — no cast needed to hand the result to a string-typed formatter —
// while a caller with Date-typed fields gets `Date | null`. `closedAt`/
// `signedAt` are declared optional (`?:`), not required-with-undefined-
// in-the-union: `reportSchema`'s `.optional()` fields infer as optional
// properties, and TS's generic type-parameter inference — unlike plain
// structural assignability — treats an optional property differently
// from a required property unioned with `undefined`, so a required-form
// signature here would reject every wire-typed caller.
export function reportTerminalAt(report) {
    return report.closedAt ?? (report.status === "signed" ? (report.signedAt ?? null) : null);
}
export function reportTerminalKind(report) {
    if (report.signedAt != null)
        return "signed";
    if (isReportTerminal(report))
        return "attested";
    return "open";
}
export const reportCreateSchema = reportSchema
    .omit({
    id: true,
    status: true,
    parties: true,
    signatures: true,
    adminData: true,
    contextMetadata: true,
    summary: true,
    pdfUrl: true,
    score: true,
    signedAt: true,
    closedAt: true,
    signedGeo: true,
    constanciaNoFirmaMotivo: true,
    constanciaNoFirmaRole: true,
    constanciaNoFirmaAt: true,
    createdAt: true,
})
    .extend({
    id: reportIdSchema.optional(),
    // Optional on create — frontend captures geo + device at "Nueva
    // acta" time and POSTs them along. If absent, the report is still
    // created (capture-with-flag).
    contextMetadata: reportContextMetadataSchema.optional(),
});
// In-progress edits — adminData and parties before signing. relatedReportId
// can be set after creation (the linkage to check-out is decided later).
export const reportUpdateSchema = reportSchema
    .pick({
    parties: true,
    adminData: true,
    relatedReportId: true,
    summary: true,
})
    .partial();
//# sourceMappingURL=report.js.map